import { useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { Loader2, Eye, EyeOff, Shield, Cpu, Database, Mail, Lock } from 'lucide-react';

export default function LoginPage() {
  const { login, loginWithGoogle } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [touched, setTouched] = useState<{ email: boolean; password: boolean }>({ email: false, password: false });

  // Validation
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const emailError = touched.email && email.length > 0 && !emailRegex.test(email) ? 'Please enter a valid email address.' : '';
  const passwordError = touched.password && password.length > 0 && password.length < 6 ? 'Password must be at least 6 characters.' : '';

  const isFormValid = emailRegex.test(email) && password.length >= 6;

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setTouched({ email: true, password: true });
    if (!isFormValid) return;

    setError('');
    setIsLoading(true);

    const result = await login(email, password);

    if (!result.success) {
      setError(result.error || 'Login failed');
    }
    setIsLoading(false);
  };

  const handleGoogleLogin = async () => {
    setError('');
    setIsGoogleLoading(true);

    const result = await loginWithGoogle();

    if (!result.success) {
      setError(result.error || 'Google sign-in failed');
    }
    setIsGoogleLoading(false);
  };

  const anyLoading = isLoading || isGoogleLoading;

  return (
    <div className="min-h-screen bg-background flex items-center justify-center relative overflow-hidden">
      {/* Background grid */}
      <div
        className="absolute inset-0 opacity-5"
        style={{
          backgroundImage: `
            linear-gradient(oklch(var(--electric-blue)) 1px, transparent 1px),
            linear-gradient(90deg, oklch(var(--electric-blue)) 1px, transparent 1px)
          `,
          backgroundSize: '40px 40px',
        }}
      />

      {/* Glow orbs */}
      <div
        className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 rounded-full opacity-10 blur-3xl pointer-events-none"
        style={{ background: 'oklch(var(--electric-blue))' }}
      />
      <div
        className="absolute bottom-1/4 right-1/4 w-64 h-64 rounded-full opacity-5 blur-3xl pointer-events-none"
        style={{ background: 'oklch(var(--success))' }}
      />

      <div className="relative z-10 w-full max-w-md px-4">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-3">
            <div className="w-12 h-12 rounded-lg bg-electric-blue/20 border border-electric-blue/30 flex items-center justify-center">
              <Cpu className="w-7 h-7 text-electric-blue" />
            </div>
            <div className="text-left">
              <h1 className="text-2xl font-black tracking-widest text-electric-blue uppercase">
                VisionInspect
              </h1>
              <p className="text-xs text-muted-foreground tracking-widest uppercase font-mono">
                AI Inspection System
              </p>
            </div>
          </div>
        </div>

        {/* Login Card */}
        <div className="bg-card border border-border rounded-lg shadow-xl overflow-hidden">
          {/* Card header */}
          <div className="px-6 py-4 border-b border-border bg-muted/30">
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4 text-electric-blue" />
              <span className="text-sm font-semibold tracking-wider uppercase text-foreground">
                Sign In to Your Account
              </span>
            </div>
          </div>

          <form onSubmit={handleLogin} className="p-6 space-y-5">
            {/* Email */}
            <div className="space-y-1.5">
              <label htmlFor="login-email" className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                Email Address
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
                <input
                  id="login-email"
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  onBlur={() => setTouched(t => ({ ...t, email: true }))}
                  placeholder="you@example.com"
                  disabled={anyLoading}
                  className="w-full bg-input border border-border rounded px-3 py-2.5 pl-10 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-electric-blue focus:border-electric-blue transition-colors disabled:opacity-50"
                  required
                  autoComplete="email"
                />
              </div>
              {emailError && (
                <p className="text-xs text-danger mt-1">{emailError}</p>
              )}
            </div>

            {/* Password */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <label htmlFor="login-password" className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                  Password
                </label>
                <button
                  type="button"
                  onClick={() => { /* Placeholder – navigate to forgot password flow */ }}
                  className="text-xs text-electric-blue hover:underline font-medium transition-colors"
                  tabIndex={-1}
                >
                  Forgot Password?
                </button>
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
                <input
                  id="login-password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  onBlur={() => setTouched(t => ({ ...t, password: true }))}
                  placeholder="Enter your password"
                  disabled={anyLoading}
                  className="w-full bg-input border border-border rounded px-3 py-2.5 pl-10 pr-10 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-electric-blue focus:border-electric-blue transition-colors disabled:opacity-50"
                  required
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {passwordError && (
                <p className="text-xs text-danger mt-1">{passwordError}</p>
              )}
            </div>

            {/* Error message */}
            {error && (
              <div className="bg-danger/10 border border-danger/30 rounded px-3 py-2.5 text-sm text-danger flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-danger mt-1.5 flex-shrink-0" />
                {error}
              </div>
            )}

            {/* Sign In button */}
            <button
              type="submit"
              disabled={anyLoading}
              className="w-full bg-electric-blue text-background font-bold py-2.5 rounded text-sm tracking-wider uppercase transition-all hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 glow-blue"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Authenticating...
                </>
              ) : (
                'Sign In'
              )}
            </button>
          </form>

          {/* Divider */}
          <div className="px-6">
            <div className="relative flex items-center">
              <div className="flex-grow border-t border-border" />
              <span className="mx-4 text-xs text-muted-foreground uppercase tracking-wider font-mono whitespace-nowrap">
                or continue with
              </span>
              <div className="flex-grow border-t border-border" />
            </div>
          </div>

          {/* Google Sign In */}
          <div className="p-6 pt-5">
            <button
              type="button"
              onClick={handleGoogleLogin}
              disabled={anyLoading}
              className="w-full flex items-center justify-center gap-3 bg-input border border-border rounded py-2.5 px-4 text-sm font-semibold text-foreground transition-all hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isGoogleLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Connecting to Google...
                </>
              ) : (
                <>
                  {/* Google G Logo SVG */}
                  <svg className="w-5 h-5" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                  </svg>
                  Continue with Google
                </>
              )}
            </button>
          </div>

          {/* Create Account link */}
          <div className="px-6 pb-5 text-center">
            <p className="text-sm text-muted-foreground">
              Don't have an account?{' '}
              <button
                type="button"
                onClick={() => { /* Placeholder – navigate to registration flow */ }}
                className="text-electric-blue hover:underline font-semibold transition-colors"
              >
                Create Account
              </button>
            </p>
          </div>

          {/* Demo credentials hint */}
          <div className="px-6 pb-5">
            <div className="bg-muted/30 border border-border rounded p-3">
              <p className="text-xs text-muted-foreground font-mono mb-2 uppercase tracking-wider">Demo Credentials</p>
              <div className="space-y-1">
                {[
                  { email: 'operator@visioninspect.io', password: 'operator123', role: 'Operator' },
                  { email: 'qcmanager@visioninspect.io', password: 'qcmanager123', role: 'QC Manager' },
                  { email: 'admin@visioninspect.io', password: 'admin123', role: 'Admin' },
                ].map(c => (
                  <button
                    key={c.email}
                    type="button"
                    onClick={() => { setEmail(c.email); setPassword(c.password); setTouched({ email: true, password: true }); }}
                    className="w-full text-left text-xs font-mono text-muted-foreground hover:text-electric-blue transition-colors py-0.5"
                  >
                    <span className="text-electric-blue">{c.role}:</span> {c.email}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* System status indicators */}
        <div className="mt-6 flex items-center justify-center gap-6">
          {[
            { icon: Cpu, label: 'AI Model', status: 'Active' },
            { icon: Database, label: 'Database', status: 'Online' },
          ].map(({ icon: Icon, label, status }) => (
            <div key={label} className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <span className="w-1.5 h-1.5 rounded-full bg-success glow-green" />
              <Icon className="w-3 h-3" />
              <span>{label}: <span className="text-success">{status}</span></span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
